import {
  Badge,
  Button,
  Card,
  Checkbox,
  Collapse,
  Group,
  Input,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
  Timeline,
} from '@mantine/core';
import {
  ONBOARDING_EXPERIENCE_OPTIONS,
  ONBOARDING_GOAL_OPTIONS,
  ONBOARDING_USAGE_PATTERN_OPTIONS,
  ONBOARDING_WEAK_AREA_OPTIONS,
  type OnboardingAiUsagePattern,
  type OnboardingExperienceLevel,
  type OnboardingGoal,
  type OnboardingWeakArea,
  type SimGraduationLoopResult,
  type SimProfile,
  type SimRoadmapResult,
} from '@prepzio/shared';
import { useMemo, useState } from 'react';

import { InfoTip } from '../components/InfoTip';
import { RoadmapView } from '../components/RoadmapView';
import {
  useDeleteSimRoadmap,
  useRoadmapDetail,
  useSimUsers,
  useSimulateGraduationLoop,
  useSimulateRoadmap,
} from '../hooks/use-content';
import { usePlural, useT, type MessageKey, type TranslateFn } from '../i18n';
import { buildMermaid } from '../lib/roadmap-mermaid';
import { errorMessage } from '../lib/ui';

/** A loop outcome, in words. Unknown outcomes print themselves. */
function outcomeLabel(outcome: string, t: TranslateFn): string {
  const key = `sim.outcome.${outcome}` as MessageKey;
  const translated = t(key);
  return translated === key ? outcome : translated;
}

const DEFAULT_PROFILE: SimProfile = {
  experienceLevel: 'junior',
  goal: 'first_job',
  aiUsagePattern: ['copy_answer'],
  weakAreas: ['debugging'],
};

/**
 * PRE-75: draft/simulation roadmap builder. Pick (or hand-craft) a persona, run
 * the REAL pipeline (onboarding → assessment with LLM-synthesized answers → real
 * scoring → roadmap) for a throwaway synthetic user, and inspect the generated
 * plan. Never touches a real learner; the result is a flagged simulation roadmap.
 */
export function SimulateScreen() {
  const t = useT();
  const usersQuery = useSimUsers();
  const simulate = useSimulateRoadmap();
  const del = useDeleteSimRoadmap();

  const [sourceUserId, setSourceUserId] = useState('');
  const [profile, setProfile] = useState<SimProfile>(DEFAULT_PROFILE);
  const [questionCount, setQuestionCount] = useState(4);
  const [completeRoadmap, setCompleteRoadmap] = useState(false);
  const [label, setLabel] = useState('');
  const [result, setResult] = useState<SimRoadmapResult | null>(null);
  const [showSource, setShowSource] = useState(false);

  const users = usersQuery.data?.items ?? [];
  const detailQuery = useRoadmapDetail(result?.roadmapId ?? null);
  const detail = detailQuery.data ?? null;
  const def = useMemo(() => (detail ? buildMermaid(detail) : ''), [detail]);

  function preloadFromUser(id: string) {
    setSourceUserId(id);
    const u = users.find((x) => x.id === id);
    if (u?.profile) setProfile(u.profile);
  }

  function toggleWeakArea(area: OnboardingWeakArea) {
    setProfile((p) => ({
      ...p,
      weakAreas: p.weakAreas.includes(area)
        ? p.weakAreas.filter((a) => a !== area)
        : [...p.weakAreas, area],
    }));
  }

  function toggleUsagePattern(pattern: OnboardingAiUsagePattern) {
    setProfile((p) => ({
      ...p,
      aiUsagePattern: p.aiUsagePattern.includes(pattern)
        ? p.aiUsagePattern.filter((a) => a !== pattern)
        : [...p.aiUsagePattern, pattern],
    }));
  }

  function run() {
    setResult(null);
    simulate.mutate(
      {
        profile,
        sourceUserId: sourceUserId || undefined,
        questionCount,
        label: label.trim() || undefined,
        completeRoadmap,
      },
      { onSuccess: (r) => setResult(r) },
    );
  }

  return (
    <Stack gap="md">
      <Group gap={6} align="center">
        <Text c="dimmed" size="sm">
          {t('sim.intro')}
        </Text>
        <InfoTip label={t('sim.infoTip')} />
      </Group>

      <Card withBorder padding="lg" radius="md">
        <Stack gap="md">
          <Select
            label={t('sim.copyPersona')}
            style={{ maxWidth: 480 }}
            value={sourceUserId || null}
            onChange={(v) => preloadFromUser(v ?? '')}
            placeholder={t('sim.blankPersona')}
            clearable
            searchable
            data={users.map((u) => ({
              value: u.id,
              label: `${u.email ?? u.name ?? u.id}${
                u.profile ? ` · ${u.profile.experienceLevel}/${u.profile.goal}` : ''
              }`,
            }))}
          />
          {usersQuery.isLoading && (
            <Text c="dimmed" size="sm">
              {t('sim.loadingUsers')}
            </Text>
          )}

          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="md">
            <Select
              label={t('sim.experience')}
              value={profile.experienceLevel}
              allowDeselect={false}
              onChange={(v) =>
                setProfile((p) => ({
                  ...p,
                  experienceLevel: (v as OnboardingExperienceLevel) ?? p.experienceLevel,
                }))
              }
              data={ONBOARDING_EXPERIENCE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            />
            <Select
              label={t('sim.goal')}
              value={profile.goal}
              allowDeselect={false}
              onChange={(v) => setProfile((p) => ({ ...p, goal: (v as OnboardingGoal) ?? p.goal }))}
              data={ONBOARDING_GOAL_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
            />
          </SimpleGrid>

          <Input.Wrapper label={t('sim.aiHabit')}>
            <Group gap="md" wrap="wrap" mt="xs">
              {ONBOARDING_USAGE_PATTERN_OPTIONS.map((o) => (
                <Checkbox
                  key={o.value}
                  label={o.label}
                  checked={profile.aiUsagePattern.includes(o.value)}
                  onChange={() => toggleUsagePattern(o.value)}
                />
              ))}
            </Group>
          </Input.Wrapper>

          <Input.Wrapper label={t('sim.weakAreas')}>
            <Group gap="md" wrap="wrap" mt="xs">
              {ONBOARDING_WEAK_AREA_OPTIONS.map((o) => (
                <Checkbox
                  key={o.value}
                  label={o.label}
                  checked={profile.weakAreas.includes(o.value)}
                  onChange={() => toggleWeakArea(o.value)}
                />
              ))}
            </Group>
          </Input.Wrapper>

          <Group align="flex-end" wrap="wrap">
            <NumberInput
              label={t('sim.questionCount')}
              min={1}
              max={8}
              value={questionCount}
              onChange={(v) => setQuestionCount(Math.max(1, Math.min(8, Number(v) || 1)))}
              style={{ width: 160 }}
            />
            <TextInput
              label={t('sim.label')}
              style={{ flex: 1, minWidth: 'min(200px, 100%)' }}
              value={label}
              onChange={(e) => setLabel(e.currentTarget.value)}
              placeholder={t('sim.labelPlaceholder')}
            />
            <Button onClick={run} loading={simulate.isPending}>
              {t('sim.run')}
            </Button>
          </Group>
          <Checkbox
            label={t('sim.alsoComplete')}
            description={t('sim.alsoCompleteHint')}
            checked={completeRoadmap}
            onChange={(e) => setCompleteRoadmap(e.currentTarget.checked)}
          />
          {simulate.isError && (
            <Text c="red" size="sm">
              {errorMessage(simulate.error)}
            </Text>
          )}
        </Stack>
      </Card>

      {result && (
        <Card withBorder padding="lg" radius="md">
          <Group justify="space-between" align="center" wrap="wrap">
            <Group gap="xs" wrap="wrap">
              <Badge variant="light">{t('sim.badge')}</Badge>
              <Badge variant="light">{t('sim.items', { count: result.itemCount })}</Badge>
              <Badge variant="light">{t('sim.aiDep', { score: result.scores.aiDependency })}</Badge>
              <Badge variant="light">
                {t('sim.explanation', { score: result.scores.explanation })}
              </Badge>
              <Badge variant="light">
                {t('sim.concept', { score: result.scores.conceptMastery })}
              </Badge>
              <Badge variant="light">
                {t('sim.answered', { count: result.scores.questionsAnswered })}
              </Badge>
              {result.completion?.ran && (
                <>
                  <Badge
                    variant="light"
                    color={result.completion.roadmapCompleted ? 'green' : 'yellow'}
                  >
                    {t(
                      result.completion.roadmapCompleted
                        ? 'sim.roadmapCompleted'
                        : 'sim.roadmapPartial',
                    )}
                  </Badge>
                  <Badge variant="light" color="teal">
                    {t('sim.stationsPractised', { count: result.completion.stationsPracticed })}
                  </Badge>
                  <Badge variant="light" color="teal">
                    {t('sim.practiceAnswers', { count: result.completion.questionsAnswered })}
                  </Badge>
                  {result.completion.stationsSkippedNoBank > 0 && (
                    <Badge variant="light" color="orange">
                      {t('sim.skippedNoBank', { count: result.completion.stationsSkippedNoBank })}
                    </Badge>
                  )}
                </>
              )}
            </Group>
            <Group gap="xs">
              <Button variant="default" onClick={() => setShowSource((v) => !v)} disabled={!detail}>
                {t(showSource ? 'sim.hideSource' : 'sim.showSource')}
              </Button>
              <Button
                variant="default"
                color="red"
                loading={del.isPending}
                onClick={() => del.mutate(result.roadmapId, { onSuccess: () => setResult(null) })}
              >
                {t('sim.deleteSim')}
              </Button>
            </Group>
          </Group>

          {/* PRE-199: the end-to-end steps the run walked. */}
          {result.steps.length > 0 && (
            <Timeline
              active={result.steps.filter((s) => s.status !== 'skipped').length - 1}
              bulletSize={16}
              lineWidth={2}
              mt="md"
            >
              {result.steps.map((s) => (
                <Timeline.Item
                  key={s.key}
                  title={s.label}
                  color={s.status === 'done' ? 'teal' : s.status === 'partial' ? 'yellow' : 'gray'}
                >
                  {s.detail && (
                    <Text c="dimmed" size="xs">
                      {s.detail}
                    </Text>
                  )}
                </Timeline.Item>
              ))}
            </Timeline>
          )}

          {/* PRE-199: LLM spend of this run (same rows land on the Costs screen). */}
          {result.cost && (
            <Card withBorder padding="sm" radius="sm" mt="md" bg="var(--mantine-color-gray-0)">
              <Group justify="space-between" wrap="wrap" gap="xs">
                <Text size="sm" fw={600}>
                  {t('sim.costTitle')}
                </Text>
                <Group gap="xs">
                  <Badge variant="light" color="grape">
                    ${result.cost.totalUsd.toFixed(4)}
                  </Badge>
                  <Badge variant="light">{t('sim.costCalls', { count: result.cost.calls })}</Badge>
                  <Badge variant="light">
                    {t('sim.costTokens', {
                      count: result.cost.inputTokens + result.cost.outputTokens,
                    })}
                  </Badge>
                </Group>
              </Group>
              {result.cost.byPurpose.length > 0 && (
                <Table mt="xs" fz="xs" verticalSpacing={4} withRowBorders={false}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t('sim.colPurpose')}</Table.Th>
                      <Table.Th ta="right">{t('sim.colCalls')}</Table.Th>
                      <Table.Th ta="right">{t('sim.colTokens')}</Table.Th>
                      <Table.Th ta="right">{t('sim.colCost')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {result.cost.byPurpose.map((p) => (
                      <Table.Tr key={p.purpose}>
                        <Table.Td>{p.purpose}</Table.Td>
                        <Table.Td ta="right">{p.calls}</Table.Td>
                        <Table.Td ta="right">{p.inputTokens + p.outputTokens}</Table.Td>
                        <Table.Td ta="right">${p.costUsd.toFixed(4)}</Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              )}
            </Card>
          )}

          {detailQuery.isLoading && (
            <Text c="dimmed" size="sm" mt="sm">
              {t('sim.loadingRoadmap')}
            </Text>
          )}
          {detailQuery.isError && (
            <Text c="red" size="sm" mt="sm">
              {errorMessage(detailQuery.error)}
            </Text>
          )}
          {detail && (
            <Stack mt="md">
              <RoadmapView detail={detail} />
            </Stack>
          )}
          {detail && (
            <Collapse expanded={showSource}>
              <pre className="code-block" style={{ marginTop: 12 }}>
                <code>{def}</code>
              </pre>
            </Collapse>
          )}

          {/* PRE-198: graduation → re-loop simulation over this sim roadmap. */}
          <GraduationLoopPanel roadmapId={result.roadmapId} />
        </Card>
      )}
    </Stack>
  );
}

/**
 * PRE-198: simulate the graduation → re-loop cycle over a sim roadmap. Feeds
 * synthetic per-skill mastery (tunable) into the REAL graduation evaluator +
 * re-loop generator, and shows each loop's outcome and the rule that fired.
 */
function GraduationLoopPanel({ roadmapId }: { roadmapId: string }) {
  const t = useT();
  const plural = usePlural();
  const loop = useSimulateGraduationLoop();
  const [baseMastery, setBaseMastery] = useState(50);
  const [improvementPerLoop, setImprovementPerLoop] = useState(15);
  const [maxLoops, setMaxLoops] = useState(5);
  const [result, setResult] = useState<SimGraduationLoopResult | null>(null);

  function run() {
    setResult(null);
    loop.mutate(
      { roadmapId, baseMastery, improvementPerLoop, maxLoops },
      { onSuccess: (r) => setResult(r) },
    );
  }

  const outcomeColor = (o: string) =>
    o === 'graduated' ? 'green' : o === 'reloop' ? 'yellow' : 'gray';

  return (
    <Card withBorder padding="md" radius="sm" mt="md" bg="var(--mantine-color-gray-0)">
      <Group gap={6} align="center" mb="xs">
        <Text fw={600} size="sm">
          {t('sim.loopTitle')}
        </Text>
        <InfoTip label={t('sim.loopTip')} />
      </Group>

      <Group align="flex-end" wrap="wrap" gap="sm">
        <NumberInput
          label={t('sim.baseMastery')}
          min={0}
          max={100}
          value={baseMastery}
          onChange={(v) => setBaseMastery(Math.max(0, Math.min(100, Number(v) || 0)))}
          style={{ width: 130 }}
        />
        <NumberInput
          label={t('sim.perLoop')}
          min={0}
          max={100}
          value={improvementPerLoop}
          onChange={(v) => setImprovementPerLoop(Math.max(0, Math.min(100, Number(v) || 0)))}
          style={{ width: 120 }}
        />
        <NumberInput
          label={t('sim.maxLoops')}
          min={1}
          max={10}
          value={maxLoops}
          onChange={(v) => setMaxLoops(Math.max(1, Math.min(10, Number(v) || 1)))}
          style={{ width: 110 }}
        />
        <Button onClick={run} loading={loop.isPending}>
          {t('sim.simulateLoop')}
        </Button>
      </Group>
      {loop.isError && (
        <Text c="red" size="sm" mt="xs">
          {errorMessage(loop.error)}
        </Text>
      )}

      {result && (
        <Stack gap="xs" mt="md">
          <Group gap="xs">
            <Badge color={outcomeColor(result.finalOutcome)}>
              {outcomeLabel(result.finalOutcome, t)}
            </Badge>
            <Text size="sm" c="dimmed">
              {plural('sim.loops_one', 'sim.loops', result.totalLoops)}
              {result.graduatedAtLoop != null &&
                t('sim.graduatedAt', { loop: result.graduatedAtLoop })}
            </Text>
          </Group>
          <Timeline bulletSize={16} lineWidth={2} active={result.steps.length - 1}>
            {result.steps.map((s) => (
              <Timeline.Item
                key={s.loop}
                color={outcomeColor(s.outcome)}
                title={t('sim.loopStep', { loop: s.loop, outcome: outcomeLabel(s.outcome, t) })}
              >
                <Text size="xs" mb={4}>
                  {s.rule}
                </Text>
                <Group gap={4} wrap="wrap">
                  {s.skills.map((skill) => (
                    <Badge
                      key={skill.skillId}
                      size="xs"
                      variant="light"
                      color={skill.passed ? 'green' : 'red'}
                      tt="none"
                    >
                      {skill.title}: {skill.mastery}
                      {skill.passed ? ' ✓' : ` < ${s.threshold}`}
                    </Badge>
                  ))}
                </Group>
                {s.nextOptions && (
                  <Text c="dimmed" size="xs" mt={4}>
                    {t('sim.nextOptions', {
                      levelUp: t(s.nextOptions.levelUpAvailable ? 'common.yes' : 'common.no'),
                      tracks: s.nextOptions.newTrackSlugs.join(', ') || t('common.none'),
                    })}
                  </Text>
                )}
              </Timeline.Item>
            ))}
          </Timeline>
        </Stack>
      )}
    </Card>
  );
}
